import {
  UserAuthorizationList,
  UserDataType,
  FallbackResult,
} from "./router.types";
import { PAGE_URLS } from "utils/constants";

/**
 * CustomFallbacks class provides a centralized way to handle different types of fallback scenarios
 * in the application's routing system.
 */

export class CustomFallbacks {
  private static DEFAULT_MESSAGE = "You don't have access to this page";
  private static FTUX_MESSAGE = "Let's get you set up first.";

  /**
   * Centralized FTUX gating logic
   * @param userData - The user's data including authorizations
   * @param permissionKey - The permission key to check
   * @param ftuxKey - The FTUX completion key to check
   * @param ftuxUrl - The FTUX URL to redirect to
   * @param successUrl - The success URL to redirect to
   * @returns FallbackResult containing the redirect URL and message
   */
  private static ftuxGate(
    userData: UserDataType,
    permissionKey: UserAuthorizationList,
    ftuxKey: UserAuthorizationList,
    ftuxUrl: string,
    successUrl: string
  ): FallbackResult {
    const hasPermission = !!userData.authorizations?.[permissionKey]?.achieved;
    const hasFTUXCompleted = !!userData.authorizations?.[ftuxKey]?.achieved;

    if (!hasPermission || !hasFTUXCompleted) {
      return { url: ftuxUrl, message: CustomFallbacks.FTUX_MESSAGE };
    }
    return { url: successUrl, message: CustomFallbacks.DEFAULT_MESSAGE };
  }

  /**
   * Handles fallback logic for sourcing-related routes based on user authorizations
   * @param userData - The user's data including authorizations
   * @returns FallbackResult containing the redirect URL and message
   */
  public static sourcingDefault(userData: UserDataType): FallbackResult {
    const authorizations = userData.authorizations;

    const sourcingEnabled =
      !!authorizations?.[UserAuthorizationList.CREATOR_SOURCING_ENABLED]
        ?.achieved;

    if (sourcingEnabled) {
      return {
        url: PAGE_URLS.SOURCING,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    } else {
      return {
        url: PAGE_URLS.SOURCING_LOCKED,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    }
  }

  public static sourcingCart(userData: UserDataType): FallbackResult {
    const authorizations = userData.authorizations;

    const sourcingEnabled =
      !!authorizations?.[UserAuthorizationList.CREATOR_SOURCING_ENABLED]
        ?.achieved;

    if (sourcingEnabled) {
      return {
        url: PAGE_URLS.SOURCING_CART,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    } else {
      return {
        url: PAGE_URLS.SOURCING_LOCKED,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    }
  }

  public static sourcingMyOrders(userData: UserDataType): FallbackResult {
    const authorizations = userData.authorizations;

    const sourcingEnabled =
      !!authorizations?.[UserAuthorizationList.CREATOR_SOURCING_ENABLED]
        ?.achieved;

    if (sourcingEnabled) {
      return {
        url: PAGE_URLS.SOURCING_MY_ORDERS,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    } else {
      return {
        url: PAGE_URLS.SOURCING_LOCKED,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    }
  }

  public static sourcingEntryPoint(userData: UserDataType): FallbackResult {
    const authorizations = userData.authorizations;

    // REMOVE THIS BLOCK AFTER NEW SOURCING IS SUCCESSFULLY LAUNCHED
    const showMaintenance =
      authorizations?.[UserAuthorizationList.RTC_FINAL_APPROVAL__BLOCKED]
        ?.achieved;
    if (showMaintenance) {
      return {
        url: PAGE_URLS.SOURCING_MAINTENANCE,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    }
    // REMOVE THIS BLOCK AFTER NEW SOURCING IS SUCCESSFULLY LAUNCHED

    const sourcingFTUXCompleted =
      !!userData?.authorizations?.[UserAuthorizationList.FTUX_SOURCING]
        ?.achieved;

    const sourcingEnabled =
      !!authorizations?.[UserAuthorizationList.CREATOR_SOURCING_ENABLED]
        ?.achieved;

    if (sourcingEnabled && sourcingFTUXCompleted) {
      return {
        url: PAGE_URLS.SOURCING,
        message: CustomFallbacks.FTUX_MESSAGE,
      };
    } else {
      return {
        url: PAGE_URLS.SOURCING_ACCESS,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    }
  }

  public static brandSourcingEntryPoint(
    userData: UserDataType
  ): FallbackResult {
    const authorizations = userData.authorizations;

    // REMOVE THIS BLOCK AFTER NEW SOURCING IS SUCCESSFULLY LAUNCHED
    const showMaintenance =
      authorizations?.[UserAuthorizationList.RTC_FINAL_APPROVAL__BLOCKED]
        ?.achieved;
    if (showMaintenance) {
      return {
        url: PAGE_URLS.SOURCING_MAINTENANCE,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    }
    // REMOVE THIS BLOCK AFTER NEW SOURCING IS SUCCESSFULLY LAUNCHED

    const brandSourcingEnabled =
      !!authorizations?.[UserAuthorizationList.CREATOR_BRAND_SOURCING_ENABLED]
        ?.achieved;

    if (brandSourcingEnabled) {
      return {
        url: PAGE_URLS.BRAND_SOURCING_HOME,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    } else {
      return {
        url: PAGE_URLS.BRAND_SOURCING_FTUX,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    }
  }

  public static sourcingMaintenance(userData: UserDataType): FallbackResult {
    const authorizations = userData.authorizations;

    // Check for RTC__MAINTENANCE flag or RTC__REFRESH flag
    if (
      authorizations?.[UserAuthorizationList.RTC__MAINTENANCE]?.achieved ||
      authorizations?.[UserAuthorizationList.RTC__REFRESH]?.achieved
    ) {
      return {
        url: PAGE_URLS.SOURCING_MAINTENANCE,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    }

    // If neither flag is found, redirect to Home page
    return {
      url: PAGE_URLS.HOME,
      message: CustomFallbacks.DEFAULT_MESSAGE,
    };
  }

  static collabFTUXAware = (userData: UserDataType) =>
    CustomFallbacks.ftuxGate(
      userData,
      UserAuthorizationList.BRAND_COLLAB__COLLAB_LIVE,
      UserAuthorizationList.FTUX_BRAND_COLLAB,
      PAGE_URLS.COLLAB_FTUX,
      PAGE_URLS.PAID_COLLABORATION
    );

  static growthFTUXAware = (userData: UserDataType) =>
    CustomFallbacks.ftuxGate(
      userData,
      UserAuthorizationList.GROWTH_V2__PRODUCT_GROWTH,
      UserAuthorizationList.FTUX_GROWTH_V2,
      PAGE_URLS.GROWTH_FTUX,
      PAGE_URLS.PRODUCT_GROWTH
    );

  static rewardsFTUXAware = (userData: UserDataType) =>
    CustomFallbacks.ftuxGate(
      userData,
      UserAuthorizationList.REWARDS__REWARDS_LIVE,
      UserAuthorizationList.FTUX_REWARDS,
      PAGE_URLS.REWARDS_FTUX,
      PAGE_URLS.WISHLINK_REWARDS
    );

  static engageFTUXAware = (userData: UserDataType) => {
    // is insta connect enabled
    const isInstaConnectEnabled =
      userData.authorizations?.[UserAuthorizationList.ENGAGE_ACTIVE]?.achieved;
    const isFacebookConnectEnabled =
      userData.authorizations?.[UserAuthorizationList.ENGAGE_FACEBOOK_ACTIVE]
        ?.achieved;

    const is_connected_insta =
      userData.authorizations?.[
        UserAuthorizationList.CREATOR_INSTAGRAM_LOGIN_COMPLETE
      ]?.achieved;
    const is_connected_facebook =
      userData.authorizations?.[
        UserAuthorizationList.CREATOR_FACEBOOK_LOGIN_COMPLETE
      ]?.achieved;

    if (!is_connected_insta && !is_connected_facebook) {
      return {
        url: PAGE_URLS.ENGAGE_FTUX,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    }

    if (!isInstaConnectEnabled && !isFacebookConnectEnabled) {
      return {
        url: PAGE_URLS.ENGAGE_FTUX,
        message: CustomFallbacks.DEFAULT_MESSAGE,
      };
    }

    return {
      url: PAGE_URLS.ENABLEENGAGE,
      message: CustomFallbacks.DEFAULT_MESSAGE,
    };
  };
}
